'use client';

import React from 'react';
import { ExternalLink, TrendingUp, FileText, ChevronDown, ChevronUp, Zap } from 'lucide-react';
import type { Signal } from './TrendFeedClient';
import StatusBadge from '@/components/ui/StatusBadge';
import SignalSparkline from './SignalSparkline';

const platformConfig = {
  reddit: {
    label: 'Reddit',
    className: 'bg-orange-500/10 text-orange-400 border border-orange-500/20',
    dot: 'bg-orange-400',
  },
  hn: {
    label: 'Hacker News',
    className: 'bg-amber-500/10 text-amber-400 border border-amber-500/20',
    dot: 'bg-amber-400',
  },
  producthunt: {
    label: 'Product Hunt',
    className: 'bg-red-500/10 text-red-400 border border-red-500/20',
    dot: 'bg-red-400',
  },
};

function ScoreRing({ score }: { score: number }) {
  const color =
    score >= 90 ? 'text-positive' : score >= 80 ? 'text-warning' : score >= 70 ? 'text-accent' : 'text-muted-foreground';
  const bgColor =
    score >= 90 ? 'bg-positive/10' : score >= 80 ? 'bg-warning/10' : score >= 70 ? 'bg-accent/10' : 'bg-muted';

  return (
    <div className={`w-12 h-12 rounded-full ${bgColor} flex flex-col items-center justify-center shrink-0`}>
      <span className={`text-sm font-bold tabular-nums leading-none ${color}`}>{score}</span>
      <span className="text-2xs text-muted-foreground">score</span>
    </div>
  );
}

interface SignalCardProps {
  signal: Signal;
  expanded: boolean;
  onToggleExpand: () => void;
}

export default function SignalCard({ signal, expanded, onToggleExpand }: SignalCardProps) {
  const pc = platformConfig[signal.platform];
  const isHighPriority = signal.score >= 80 && signal.status === 'unused';

  return (
    <div
      className={`card-elevated flex flex-col transition-all duration-200 hover:border-border/60 ${
        isHighPriority ? 'pulse-amber border-warning/30' : ''
      }`}
    >
      {/* Card Header */}
      <div className="p-4 flex flex-col gap-3">
        {/* Top row: platform + score + status */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex flex-col gap-1.5 flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-2xs font-semibold font-mono ${pc.className}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${pc.dot}`} />
                {pc.label}
              </span>
              {signal.subreddit && (
                <span className="text-2xs text-muted-foreground font-mono">{signal.subreddit}</span>
              )}
              {isHighPriority && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-2xs font-semibold bg-warning/10 text-warning border border-warning/20">
                  <Zap size={9} />
                  High Priority
                </span>
              )}
            </div>
            <p className="text-sm font-medium text-foreground leading-snug">{signal.topic}</p>
            <p className="text-2xs font-mono text-muted-foreground">{signal.keyword}</p>
          </div>
          <ScoreRing score={signal.score} />
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-3 gap-2">
          <div className="bg-secondary rounded-md px-2 py-1.5 text-center">
            <p className="text-xs font-bold tabular-nums text-foreground">{signal.postCount.toLocaleString()}</p>
            <p className="text-2xs text-muted-foreground">posts</p>
          </div>
          <div className="bg-secondary rounded-md px-2 py-1.5 text-center">
            <p className="text-xs font-bold tabular-nums text-foreground">
              {signal.commentCount >= 1000
                ? `${(signal.commentCount / 1000).toFixed(1)}K`
                : signal.commentCount}
            </p>
            <p className="text-2xs text-muted-foreground">comments</p>
          </div>
          <div className="bg-secondary rounded-md px-2 py-1.5 text-center">
            <div className="flex items-center justify-center gap-0.5">
              <TrendingUp size={10} className="text-positive" />
              <p className="text-xs font-bold tabular-nums text-positive">+{signal.velocity24h}</p>
            </div>
            <p className="text-2xs text-muted-foreground">24h vel.</p>
          </div>
        </div>

        {/* Sparkline */}
        <div className="flex items-center gap-3">
          <div className="flex-1">
            <SignalSparkline data={signal.velocityHistory} signalId={signal.id} />
          </div>
          <div className="text-right">
            <p className="text-2xs text-muted-foreground font-mono">8h trend</p>
          </div>
        </div>

        {/* Keywords */}
        <div className="flex flex-wrap gap-1">
          {signal.keywords.map((kw) => (
            <span
              key={`kw-${signal.id}-${kw}`}
              className="px-1.5 py-0.5 bg-muted rounded text-2xs text-muted-foreground font-mono"
            >
              #{kw}
            </span>
          ))}
        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-border px-4 py-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <StatusBadge variant={signal.status} />
          {signal.linkedScript && (
            <div className="flex items-center gap-1 text-2xs text-muted-foreground max-w-[160px]">
              <FileText size={10} className="shrink-0 text-primary" />
              <span className="truncate text-primary/80">{signal.linkedScriptId}</span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-2xs text-muted-foreground font-mono">{signal.capturedAt}</span>
          <button
            onClick={onToggleExpand}
            className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-all duration-150"
            title={expanded ? 'Collapse top posts' : 'Show top posts'}
          >
            {expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
          </button>
        </div>
      </div>

      {/* Expanded: Top Posts */}
      {expanded && (
        <div className="border-t border-border px-4 py-3 space-y-2 fade-in">
          <p className="text-2xs font-semibold uppercase tracking-widest text-muted-foreground mb-2">
            Top Posts
          </p>
          {signal.topPosts.map((post, idx) => (
            <div
              key={`post-${signal.id}-${idx}`}
              className="flex items-start gap-2 p-2 rounded-md bg-secondary hover:bg-muted/50 transition-colors group"
            >
              <span className="text-2xs font-mono text-muted-foreground w-4 shrink-0 mt-0.5">
                {idx + 1}.
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-foreground leading-snug line-clamp-2">{post.title}</p>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-2xs text-muted-foreground font-mono tabular-nums">
                    ▲ {post.upvotes.toLocaleString()}
                  </span>
                </div>
              </div>
              <a
                href={post.url}
                target="_blank"
                rel="noopener noreferrer"
                className="shrink-0 p-1 rounded text-muted-foreground hover:text-accent transition-colors opacity-0 group-hover:opacity-100"
              >
                <ExternalLink size={11} />
              </a>
            </div>
          ))}

          {signal.status === 'unused' && (
            <button className="w-full mt-2 py-2 rounded-md bg-primary/10 border border-primary/20 text-primary text-xs font-semibold hover:bg-primary/20 transition-all duration-150 active:scale-95">
              Generate Script from Signal
            </button>
          )}
        </div>
      )}
    </div>
  );
}